import { MouseEvent, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Skeleton,
  Theme,
  Typography,
  styled,
  useMediaQuery,
  ListItemText,
  MenuProps,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { RequestDetail } from "../../../interfaces";
import { theme } from "../../../config";
import { getUnitForQuantity } from "../../../utils";

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  backgroundColor: theme.pallete.background.white,
  gap: "4px",
  padding: "8px 16px",
});

const Row = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
});

const InfoColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  gap: "4px",
});

const ActionColumn = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  flexShrink: 0,
});

const ProductName = styled(Typography)({
  fontWeight: 500,
  color: theme.pallete.text.black,
  textWrap: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ProductDetails = styled(Typography)({
  fontSize: "14px",
  color: theme.pallete.text.gray,
});

const TreatmentDetail = styled(Typography)({
  fontSize: "14px",
  color: theme.pallete.text.gray,
});

const ActionButton = styled(IconButton)({
  color: theme.pallete.text.primary,
  padding: 0,
});

const ActionMenu = styled((props: MenuProps) => (
  <Menu
    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    transformOrigin={{ vertical: "top", horizontal: "right" }}
    slotProps={{
      paper: {
        style: {
          width: "200px",
        },
      },
    }}
    {...props}
  />
))({
  "& .MuiPaper-root": {
    boxShadow: "0px 2px 12px rgba(0,0,0,0.1)",
  },
});

export type PrescriptionCard = Pick<
  RequestDetail,
  | "name"
  | "activePrinciple"
  | "concentration"
  | "laboratory"
  | "posologyQuantity"
  | "posologyFormat"
  | "frequencyQuantity"
  | "frequencyPeriod"
  | "durationQuantity"
  | "durationPeriod"
  | "quantityToDispense"
  | "indications"
>;

interface PrescriptionDetailCardProps {
  detail: PrescriptionCard;
  handleDelete?: (detail: PrescriptionCard) => void;
  handleEdit?: (detail: PrescriptionCard) => void;
}

export function PrescriptionDetailCard(props: PrescriptionDetailCardProps) {
  const { detail, handleDelete, handleEdit } = props;
  const {
    activePrinciple,
    concentration,
    name,
    laboratory,
    posologyQuantity,
    posologyFormat,
    frequencyQuantity,
    frequencyPeriod,
    durationQuantity,
    durationPeriod,
    quantityToDispense,
    indications,
  } = detail;

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMobileActionMenuOpen = Boolean(menuAnchorEl);
  const cardTitle = useMemo(() => {
    const hasLessThan4ActivePrinciples =
      activePrinciple && activePrinciple.split(";").length < 4;
    const hasLessThan4Concentrations =
      concentration && concentration.split(";").length < 4;
    if (hasLessThan4ActivePrinciples || hasLessThan4Concentrations) {
      return `${activePrinciple ?? ""} ${concentration ?? ""}`.trim();
    } else {
      return name;
    }
  }, [name, activePrinciple, concentration]);

  const cardSubTitle = useMemo(() => {
    const parsedName =
      name && activePrinciple && name !== activePrinciple && name !== cardTitle
        ? name
        : null;
    const parsedLaboratory =
      laboratory && laboratory !== "TO_DO" ? laboratory : null;
    return [parsedName, parsedLaboratory].filter(Boolean).join(" · ");
  }, [name, activePrinciple, laboratory, cardTitle]);

  const consumptionDetail = useMemo(() => {
    if (posologyQuantity && posologyFormat) {
      return `${posologyQuantity} ${getUnitForQuantity(
        posologyFormat,
        posologyQuantity,
      )} cada ${frequencyQuantity} ${getUnitForQuantity(
        frequencyPeriod,
        frequencyQuantity,
      )}`;
    }
    return "";
  }, [posologyQuantity, posologyFormat, frequencyQuantity, frequencyPeriod]);

  const treatmentDuration = useMemo(() => {
    if (durationQuantity && durationPeriod) {
      return `Tratamiento por ${durationQuantity} ${getUnitForQuantity(
        durationPeriod,
        durationQuantity,
      )}`;
    }
    return "";
  }, [durationQuantity, durationPeriod]);

  const isTabletOrCapsule = useMemo(() => {
    return posologyFormat === "comprimido" || posologyFormat === "cápsula";
  }, [posologyFormat]);

  const quantityToDispenseText = useMemo(() => {
    if (quantityToDispense) {
      return `Cant. a dispensar: ${quantityToDispense} ${
        isTabletOrCapsule
          ? getUnitForQuantity("caja", quantityToDispense)
          : getUnitForQuantity("unidad", quantityToDispense)
      }`;
    }
    return "";
  }, [isTabletOrCapsule, quantityToDispense]);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const hasActions = Boolean(handleEdit || handleDelete);

  return (
    <Container>
      <Row>
        <InfoColumn>
          {cardTitle && <ProductName>{cardTitle}</ProductName>}
          {cardSubTitle && <ProductDetails>{cardSubTitle}</ProductDetails>}
          {consumptionDetail && (
            <TreatmentDetail>{consumptionDetail}</TreatmentDetail>
          )}
          {treatmentDuration && (
            <TreatmentDetail>{treatmentDuration}</TreatmentDetail>
          )}
          {quantityToDispenseText && (
            <TreatmentDetail>{quantityToDispenseText}</TreatmentDetail>
          )}
        </InfoColumn>
        {isMobile ? (
          hasActions && (
            <ActionColumn>
              <ActionButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </ActionButton>
              <ActionMenu
                anchorEl={menuAnchorEl}
                open={isMobileActionMenuOpen}
                onClose={handleMenuClose}
              >
                {handleEdit && (
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      handleEdit(detail);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <EditOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                  </MenuItem>
                )}
                {handleDelete && (
                  <MenuItem
                    disableRipple
                    onClick={() => {
                      handleDelete(detail);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Eliminar</ListItemText>
                  </MenuItem>
                )}
              </ActionMenu>
            </ActionColumn>
          )
        ) : (
          <>
            {handleEdit && (
              <ActionColumn>
                <ActionButton onClick={() => handleEdit(detail)}>
                  <EditOutlinedIcon />
                </ActionButton>
              </ActionColumn>
            )}
            {handleDelete && (
              <ActionColumn>
                <ActionButton onClick={() => handleDelete(detail)}>
                  <DeleteOutlinedIcon />
                </ActionButton>
              </ActionColumn>
            )}
          </>
        )}
      </Row>
      {indications && (
        <>
          <Divider />
          <TreatmentDetail>{indications}</TreatmentDetail>
        </>
      )}
    </Container>
  );
}

PrescriptionDetailCard.Skeleton = function PrescriptionDetailCardSkeleton() {
  return (
    <Container>
      <Row>
        <InfoColumn>
          <ProductName>
            <Skeleton variant="text" width="90%" />
          </ProductName>
          <ProductDetails>
            <Skeleton variant="text" width="90%" />
          </ProductDetails>
          <TreatmentDetail>
            <Skeleton variant="text" width="90%" />
          </TreatmentDetail>
        </InfoColumn>
        <ActionColumn></ActionColumn>
      </Row>
    </Container>
  );
};
